import { inrMoney } from '@/lib/format'
import type { OrderItem, ValidationIssue } from '@/lib/vision/schemas'

/**
 * v1 PO-drawer check grammar, mapped onto Vision's issue list.
 *
 * Pulse v1's server persisted a row per check (ITEM / PRICE / HSN / CASEPACK /
 * LOCATION) including PASS. Vision only persists *issues* (FAIL / HOLD / WARN).
 * The helpers reconstruct chips: a family with no issue on a line Vision
 * evaluates renders as PASS. Pulse shows ITEM / CASEPACK / PRICE / HSN only —
 * LOCATION stays a Vision master and is not a Pulse check.
 *
 * `MASTER_DATA_UNAVAILABLE` is a Vision-only stub for those unported masters
 * and is filtered out — v1 never showed "we didn't check this".
 *
 * Line join (one source of truth for the Issues list AND the PRICE/ITEM chips):
 *   1. `issue.orderItemId` ↔ `item.id` when Vision emits line ids
 *   2. diagnostic text (`Line N` / `Channel SKU …`) for older messages
 *   3. PRICE_MISMATCH `stated ₹X` ↔ unique/ordered line `netAmount` so today's
 *      prod payload (ids on issues, no ids on items, no SKU in the message)
 *      still badges the correct row
 */

export type CheckFamily = 'ITEM' | 'CASEPACK' | 'PRICE' | 'HSN' | 'LOCATION'

export type CheckOutcome = 'PASS' | 'WARN' | 'FAIL' | 'HOLD'

export interface Check {
  family: CheckFamily
  outcome: CheckOutcome
  reason: string | null
  detail: string | null
}

export interface PriceMismatch {
  stated: number
  agreed: number
  deltaPct: number
}

export interface BoundIssue {
  issue: ValidationIssue
  item: OrderItem | null
}

const FAMILY_BY_CODE: Record<string, CheckFamily> = {
  ITEM_UNMAPPED: 'ITEM',
  MAPPING_AMBIGUOUS: 'ITEM',
  ITEM_BLOCKED: 'ITEM',
  ITEM_NOT_IN_BC: 'ITEM',
  ITEM_BLOCKED_BC: 'ITEM',
  CASECONV_MISSING: 'CASEPACK',
  CASEPACK_ROUNDING: 'CASEPACK',
  PRICE_MASTER_MISSING: 'PRICE',
  PRICE_MISMATCH: 'PRICE',
  MRP_CONFLICT: 'PRICE',
  HSN_MISSING: 'HSN',
  HSN_CONFLICT: 'HSN',
  HSN_PREFIX_MATCH: 'HSN',
  TAX_RATE_CONFLICT: 'HSN',
  LOCATION_UNMAPPED: 'LOCATION',
  LOCATION_UNSERVICEABLE: 'LOCATION',
  LOCATION_AUTO_ADDED: 'LOCATION',
}

const FAMILY_ORDER: CheckFamily[] = ['ITEM', 'CASEPACK', 'PRICE', 'HSN', 'LOCATION']

const STUB_CODE = 'MASTER_DATA_UNAVAILABLE'

const STATED_VS_AGREED =
  /stated\s*₹\s*([\d,.]+)\s*vs\s*agreed\s*₹\s*([\d,.]+)/i

const AMOUNT_EPS = 0.005

export function isStubIssue(issue: ValidationIssue): boolean {
  return issue.code === STUB_CODE
}

export function familyForCode(code: string): CheckFamily | null {
  if (FAMILY_BY_CODE[code]) return FAMILY_BY_CODE[code]
  if (code.startsWith('ITEM') || code.startsWith('MAPPING')) return 'ITEM'
  if (code.startsWith('CASE')) return 'CASEPACK'
  if (code.startsWith('PRICE') || code.startsWith('MRP')) return 'PRICE'
  if (code.startsWith('HSN') || code.startsWith('TAX') || code.startsWith('GST')) return 'HSN'
  if (code.startsWith('LOCATION')) return 'LOCATION'
  return null
}

export function isBlockingOutcome(outcome: string): boolean {
  return outcome === 'FAIL' || outcome === 'HOLD'
}

/**
 * Checks worth painting on a line. `checksForLine` always emits ITEM / CASEPACK /
 * PRICE / HSN, so a healthy line carried four green chips that said nothing while
 * the status pill next to them already said it. Only a non-PASS outcome earns one.
 */
export function actionableChecks(checks: readonly Check[]): Check[] {
  return checks.filter(check => check.outcome !== 'PASS')
}

/** Reason code → sentence case ("PRICE_MISMATCH" → "Price mismatch"). v1 Issues chips. */
export function friendlyReason(code: string): string {
  return code.replace(/_/g, ' ').toLowerCase().replace(/^./, s => s.toUpperCase())
}

export function isHeaderIssue(issue: ValidationIssue): boolean {
  return issue.orderItemId == null
}

export function parsePriceMismatch(message: string | null | undefined): PriceMismatch | null {
  if (!message) return null
  const match = STATED_VS_AGREED.exec(message)
  if (!match) return null
  const stated = Number(match[1]!.replace(/,/g, ''))
  const agreed = Number(match[2]!.replace(/,/g, ''))
  if (!Number.isFinite(stated) || !Number.isFinite(agreed)) return null
  const deltaPct =
    agreed === 0 ? 0 : ((stated - agreed) / Math.abs(agreed)) * 100
  return { stated, agreed, deltaPct }
}

export function formatDeltaPct(deltaPct: number): string {
  const rounded = Math.round(deltaPct * 10) / 10
  const sign = rounded > 0 ? '+' : ''
  return `${sign}${rounded.toFixed(1)}%`
}

export function formatIssueDetail(issue: ValidationIssue): string {
  const price = parsePriceMismatch(issue.message)
  if (price) {
    return `${friendlyReason(issue.code)}: stated ${inrMoney(price.stated)} vs agreed ${inrMoney(price.agreed)} (${formatDeltaPct(price.deltaPct)})`
  }
  return issue.message ?? friendlyReason(issue.code)
}

export function issueLineLabel(item: OrderItem, title?: string | null): string {
  const name = (item.displayName || title || item.channelSku).trim()
  return `#${item.itemSequenceNumber} ${name} · ${item.channelSku}`
}

function amountsClose(a: number, b: number): boolean {
  return Math.abs(a - b) < AMOUNT_EPS
}

function lineNetAmount(item: OrderItem): number | null {
  if (item.netAmount?.amount == null) return null
  const value = Number(item.netAmount.amount)
  return Number.isFinite(value) ? value : null
}

/**
 * Exact join — id, `Line N`, or `Channel SKU` in the diagnostic. Does not use
 * stated-amount fallback (that needs exclusive assignment across the run).
 */
export function issueTouchesItem(issue: ValidationIssue, item: OrderItem): boolean {
  if (isHeaderIssue(issue) || isStubIssue(issue)) return false
  if (item.id && issue.orderItemId && item.id === issue.orderItemId) return true
  const msg = issue.message ?? ''
  const seq = /^Line (\d+)\b/.exec(msg)
  if (seq && Number(seq[1]) === item.itemSequenceNumber) return true
  if (item.channelSku && msg.includes(`Channel SKU ${item.channelSku}`)) return true
  return false
}

/**
 * Bind every issue to at most one line. Shared by the Issues list and the
 * per-line chips so they cannot disagree.
 */
export function bindIssues(
  issues: readonly ValidationIssue[],
  items: readonly OrderItem[],
): BoundIssue[] {
  const claimed = new Set<number>()
  const bound: BoundIssue[] = issues.map(issue => {
    if (isHeaderIssue(issue) || isStubIssue(issue)) {
      return { issue, item: null }
    }
    const hit = items.find(row => issueTouchesItem(issue, row))
    if (hit) {
      claimed.add(hit.itemSequenceNumber)
      return { issue, item: hit }
    }
    return { issue, item: null }
  })

  for (const row of bound) {
    if (row.item) continue
    if (isHeaderIssue(row.issue) || isStubIssue(row.issue)) continue
    const price = parsePriceMismatch(row.issue.message)
    if (!price) continue
    const match = items.find(item => {
      if (claimed.has(item.itemSequenceNumber)) return false
      const net = lineNetAmount(item)
      return net != null && amountsClose(net, price.stated)
    })
    if (match) {
      claimed.add(match.itemSequenceNumber)
      row.item = match
    }
  }
  return bound
}

export function issuesForItem(
  item: OrderItem,
  issues: readonly ValidationIssue[],
  items: readonly OrderItem[],
): ValidationIssue[] {
  return bindIssues(issues, items)
    .filter(row => row.item?.itemSequenceNumber === item.itemSequenceNumber)
    .map(row => row.issue)
}

export function lineBlockedFromAccept(
  item: OrderItem,
  issues: readonly ValidationIssue[],
  items: readonly OrderItem[] = [item],
): boolean {
  const headerBlocked = issues.some(
    i =>
      isHeaderIssue(i) &&
      !isStubIssue(i) &&
      familyForCode(i.code) !== 'LOCATION' &&
      isBlockingOutcome(i.severity),
  )
  if (headerBlocked) return true
  return checksForLine(item, issues, items).some(c => isBlockingOutcome(c.outcome))
}

export function orderIsAcknowledged(items: readonly OrderItem[]): boolean {
  return items.length > 0 && items.every(item => Boolean(item.ackCode))
}

/**
 * Amazon (incl. amazon_now) submits Vendor Central ACK. Flipkart and Flipkart
 * Minutes are already marketplace-acked on ingest (`ACKNOWLEDGED` / Ackd chip) —
 * no Pulse ACK action. Warehouse release still runs on both.
 */
export function channelHasMarketplaceAck(channel: string | undefined | null): boolean {
  const c = (channel ?? 'amazon').toLowerCase().replace(/-/g, '_')
  return c === 'amazon' || c === 'amazon_now'
}

/** Flipkart lands ACKNOWLEDGED without line ack codes. Amazon uses per-line ack. */
export function orderReadyToWarehouse(
  channel: string | undefined | null,
  status: string | undefined | null,
  items: readonly OrderItem[],
): boolean {
  if (channelHasMarketplaceAck(channel)) {
    return orderIsAcknowledged(items)
  }
  const s = (status ?? '').toUpperCase()
  return (
    s === 'ACKNOWLEDGED' ||
    s === 'FULLY_ACKED' ||
    s === 'PARTIALLY_ACKED' ||
    s === 'PARTIALLY_FULFILLED' ||
    s === 'FULFILLED'
  )
}

/**
 * Warehouse-send eligibility. Amazon needs {@code Accepted}. Flipkart has no
 * marketplace line ACK — every line is sendable except Rejected.
 */
export function lineEligibleForWarehouse(
  item: Pick<OrderItem, 'ackCode'>,
  channel?: string | null,
): boolean {
  if (item.ackCode === 'Rejected') return false
  if (!channelHasMarketplaceAck(channel)) return true
  return item.ackCode === 'Accepted'
}

function toCheck(issue: ValidationIssue): Check | null {
  const family = familyForCode(issue.code)
  if (!family) return null
  const outcome: CheckOutcome =
    issue.severity === 'FAIL' || issue.severity === 'HOLD' || issue.severity === 'WARN'
      ? issue.severity
      : 'WARN'
  return {
    family,
    outcome,
    reason: issue.code,
    detail: formatIssueDetail(issue),
  }
}

function sortChecks(checks: Check[]): Check[] {
  return [...checks].sort(
    (a, b) => FAMILY_ORDER.indexOf(a.family) - FAMILY_ORDER.indexOf(b.family),
  )
}

/** Header-level chips. LOCATION is not a Pulse check; leftover Vision LOCATION issues are ignored. */
export function headerChecks(issues: readonly ValidationIssue[]): Check[] {
  const real = issues.filter(
    i => isHeaderIssue(i) && !isStubIssue(i) && familyForCode(i.code) !== 'LOCATION',
  )
  return sortChecks(real.map(toCheck).filter((c): c is Check => c != null))
}

/**
 * Per-line chips. ITEM / CASEPACK / PRICE / HSN always appear (Vision evaluates
 * them). A family with no issue on this line renders PASS. LOCATION is not a
 * Pulse check and is never painted.
 */
export function checksForLine(
  item: OrderItem,
  issues: readonly ValidationIssue[],
  items: readonly OrderItem[] = [item],
): Check[] {
  const mine = issuesForItem(item, issues, items)
  const families = new Set<CheckFamily>(['ITEM', 'CASEPACK', 'PRICE', 'HSN'])
  for (const issue of mine) {
    const family = familyForCode(issue.code)
    if (family && family !== 'LOCATION') families.add(family)
  }
  const checks: Check[] = []
  for (const family of FAMILY_ORDER) {
    if (!families.has(family)) continue
    const hit = mine.find(i => familyForCode(i.code) === family)
    checks.push(
      hit
        ? (toCheck(hit) ?? { family, outcome: 'PASS', reason: null, detail: null })
        : { family, outcome: 'PASS', reason: null, detail: null },
    )
  }
  return checks
}

export function priceMismatchForItem(
  item: OrderItem,
  issues: readonly ValidationIssue[],
  items: readonly OrderItem[] = [item],
): PriceMismatch | null {
  const hit = issuesForItem(item, issues, items).find(i => i.code === 'PRICE_MISMATCH')
  return hit ? parsePriceMismatch(hit.message) : null
}

export interface BlockingIssue {
  check: Check
  item: OrderItem | null
  itemLabel: string | null
  itemSequenceNumber: number | null
  price: PriceMismatch | null
}

/** FAIL/HOLD only — v1's "Issues (N)" list, the why behind "Has issues". */
export function blockingIssues(
  issues: readonly ValidationIssue[],
  items: readonly OrderItem[],
  nameOf: (item: OrderItem) => string,
): BlockingIssue[] {
  const out: BlockingIssue[] = []
  for (const { issue, item } of bindIssues(issues, items)) {
    if (isStubIssue(issue)) continue
    const check = toCheck(issue)
    if (!check || check.family === 'LOCATION' || !isBlockingOutcome(check.outcome)) continue
    out.push({
      check,
      item,
      itemLabel: item ? issueLineLabel(item, nameOf(item)) : null,
      itemSequenceNumber: item?.itemSequenceNumber ?? null,
      price: parsePriceMismatch(issue.message),
    })
  }
  return out
}
